import { Stack, Typography } from '@mui/material';
import { HealthCenter, PerformerRequest, Unit } from '@silbo/core';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useLiveQuery from '../../../hooks/useLiveQuery';
import PageContainer from '../components/PageContainer';
import RequestCard from '../components/RequestCard';
import { getRequestsQuery, getRequestsQueryOptions, getUnitsQueryOptions } from '../queries';

interface RequestsPageProps {
  userUnitIds: string[];
  healthCenter: HealthCenter | undefined;
}

const RequestsPage = ({ userUnitIds, healthCenter }: RequestsPageProps) => {
  const { t } = useTranslation('bed-management');

  const { data: units } = useSuspenseQuery(getUnitsQueryOptions('bedManagement', healthCenter, userUnitIds));
  const { data: requests } = useSuspenseQuery(getRequestsQueryOptions(units));

  useLiveQuery(getRequestsQuery(units));

  const requestsByUnit: Record<string, { unit: Unit; requests: PerformerRequest[] }> = useMemo(() => {
    const requestsByUnit: Record<string, { unit: Unit; requests: PerformerRequest[] }> = {};

    for (const request of requests) {
      const unit = request.performer;
      if (requestsByUnit[unit.id]) {
        requestsByUnit[unit.id].requests.push(request);
      } else {
        requestsByUnit[unit.id] = { unit, requests: [request] };
      }
    }
    return requestsByUnit;
  }, [requests]);

  return (
    <PageContainer title={t('requests.title')} badgeCount={requests.length}>
      <Stack spacing={3}>
        {requests.length === 0 ? (
          <Typography color="text.secondary">{t('requests.noRequests')}</Typography>
        ) : (
          <Stack spacing={4}>
            {Object.entries(requestsByUnit).map(([unitId, { unit, requests }]) => (
              <Stack spacing={3} key={unitId}>
                <Typography fontWeight={600}>{unit.name}</Typography>
                <Stack spacing={2}>
                  {requests.map((request) => (
                    <RequestCard key={request.id} request={request} />
                  ))}
                </Stack>
              </Stack>
            ))}
          </Stack>
        )}
      </Stack>
    </PageContainer>
  );
};

export default RequestsPage;
